/**
 * apps/command i18n — MBT's `t()`.
 *
 * ============================================================================
 * ONE DICTIONARY. NO LOCALE SWITCHER. STILL NO HARDCODED STRINGS.
 * ============================================================================
 *
 * MBT §2.6 rules that the panel is **uz-Latn only**: "one dictionary, no locale
 * switcher. The users are hokimlik officials working in the state language, and
 * four-locale parity across ~1,500 admin strings would cost weeks and buy
 * nothing."
 *
 * That decides which dictionaries exist. It does NOT relax the engineering
 * rule, and §2.6 is explicit about the distinction: "still **no hardcoded
 * strings.** MBT renders through `t()` against a single uz-Latn dictionary, so
 * a second language can be added later without a rewrite. `i18n:parity` does
 * not apply to the MBT dictionary; `no-hardcoded-strings` does."
 *
 * So this module is the same `t()` the murojaat panel has, minus the locale
 * argument. `npm run mbt:check` is the gate that keeps it honest.
 *
 * ============================================================================
 * WHY THE FILE LIVES IN `packages/i18n/mbt/` AND NOT IN THIS APP.
 * ============================================================================
 *
 * Copy is copy: it belongs in the package that owns copy, beside the four
 * citizen locales, so a translator opens one directory rather than hunting
 * through an app's source tree. And it is in `mbt/` rather than `locales/`
 * because `scripts/parity.mjs` reads `locales/` — putting MBT's single
 * dictionary there would fail the four-locale key-set check on its first key.
 * The separation is structural, not a configured exclusion somebody can forget
 * to update.
 */
import dictionary from '@navoiy/i18n/mbt/uz-Latn.json';

type Dictionary = Record<string, unknown>;

const DICTIONARY = dictionary as Dictionary;

/**
 * Resolve a key, allowing a dictionary entry to CONTAIN dots.
 *
 * ============================================================================
 * WHY THIS IS NOT JUST `key.split('.')`, WHICH IS WHAT IT WAS.
 * ============================================================================
 *
 * Some of the things this panel names have dots in their own identifiers.
 * `app.restart` is not a path — it is a kiosk command's id, one token, and the
 * dictionary quite reasonably stores it as one key:
 *
 *     "command": { "type": { "app.restart": "Dasturni qayta ishga tushirish" } }
 *
 * A naive split walked `command → type → app → restart`, found nothing at
 * `app`, and returned undefined. Every caller does
 * `hasKey(k) ? t(k) : rawValue`, so the failure was SILENT and shipped: the
 * bulk composer, the remote-action buttons and every command row in the panel
 * printed `content.refresh` and `machine.reboot` at a governor, in English, in
 * a Uzbek-only interface — for as long as those screens have existed.
 *
 * `npm run mbt:check` did not catch it because it flattens the JSON to dotted
 * strings and compares those, so `command.type.app.restart` looked present. It
 * IS present. It was simply unreachable.
 *
 * So: at each level, try the WHOLE remaining key as a literal first, and only
 * then descend one segment. Both shapes resolve, nothing that worked before
 * changes, and a key with dots in it is no longer a trap.
 */
function lookup(key: string): string | undefined {
  let node: unknown = DICTIONARY;
  let rest = key;
  for (;;) {
    if (node === null || typeof node !== 'object') return undefined;
    const record = node as Record<string, unknown>;
    const literal = record[rest];
    if (typeof literal === 'string') return literal;
    const dot = rest.indexOf('.');
    if (dot === -1) return undefined;
    node = record[rest.slice(0, dot)];
    rest = rest.slice(dot + 1);
  }
}

/**
 * `{name}` placeholders, substituted positionally by name.
 *
 * A missing parameter leaves its placeholder VISIBLE rather than blanking it.
 * «{code} kioski aloqada emas» with the code missing reads as a bug and gets
 * fixed; «kioski aloqada emas» reads as a finished sentence about no kiosk in
 * particular, and would sit in front of a governor for a year.
 */
function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name];
    return value === undefined ? whole : String(value);
  });
}

/**
 * The panel's only way to produce a user-visible string.
 *
 * A MISSING KEY RETURNS THE KEY. Not an empty string, and not a thrown error: a
 * blank label on a wall display is invisible until somebody in a meeting asks
 * what the empty box is, while `fleet.metric.disk` on screen is unmistakably a
 * missing string and names the entry to add. The gate catches these before they
 * ship; this is what happens if one ever gets past it.
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const template = lookup(key);
  if (template === undefined) {
    if (import.meta.env.DEV) console.warn(`[i18n] missing MBT key: ${key}`);
    return key;
  }
  return interpolate(template, params);
}

/** Exported for the dictionary-coverage gate, which asserts every key resolves. */
export function hasKey(key: string): boolean {
  return lookup(key) !== undefined;
}
